import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import { Avatar, Field, useToast, Modal, StatusPill, Tabs, Empty, PageHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import LocationFields from '../../components/LocationFields.jsx';
import AvatarUpload from '../../components/AvatarUpload.jsx';
import PhoneField from '../../components/PhoneField.jsx';
import ComplaintDetail from '../../components/ComplaintDetail.jsx';
import { isValidPhone } from '../../lib/validation.js';
import { relativeTime } from '../../lib/format.js';

const QUICK_LINKS = [
  { to: '/bookings', icon: 'clipboard', title: 'My bookings', sub: 'Requests and past visits' },
  { to: '/search', icon: 'search', title: 'Find a nurse', sub: 'Browse verified profiles' },
];

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState('personal');
  const [form, setForm] = useState({ name: '', phone: '', gender: '', address: '', city: '', state: '', lat: null, lng: null });
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [complaints, setComplaints] = useState([]);
  const [showComplaint, setShowComplaint] = useState(false);
  const [complaint, setComplaint] = useState({ subject: '', message: '' });
  const [openComplaint, setOpenComplaint] = useState(null);

  const loadComplaints = useCallback(() => api.get('/api/complaints').then(setComplaints).catch(() => {}), []);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        phone: user.phone || '',
        gender: user.gender || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        lat: user.lat ?? null,
        lng: user.lng ?? null,
      });
    }
    loadComplaints();
  }, [user, loadComplaints]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (form.phone && !isValidPhone(form.phone)) return toast.error('Enter a valid phone number.');
    setBusy(true);
    try {
      await api.put('/api/auth/me', form);
      await refresh();
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.put('/api/auth/password', pw);
      setPw({ currentPassword: '', newPassword: '' });
      toast.success('Password changed.');
    } catch (err) {
      toast.error(err);
    }
  };

  const submitComplaint = async () => {
    try {
      const created = await api.post('/api/complaints', complaint);
      setComplaints((c) => [created, ...c]);
      setComplaint({ subject: '', message: '' });
      setShowComplaint(false);
      toast.success('Complaint submitted — an administrator will review it.');
    } catch (err) {
      toast.error(err);
    }
  };

  if (!user) return null;

  const openCount = complaints.filter((c) => c.status === 'open').length;

  return (
    <>
      <PageHeader
        title={user.name}
        subtitle={user.email}
        avatar={<Avatar name={user.name} avatar={user.avatar} size="lg" ring />}
        badge={<span className="pill pill-accent">{user.role}</span>}
      >
        <button className="btn btn-ghost" onClick={logout}>
          <Icon name="logout" /> Sign out
        </button>
      </PageHeader>

      {/* Quick jumps so the profile page is a hub, not a dead end. */}
      <div className="quick-actions mb-3">
        {QUICK_LINKS.map((a) => (
          <Link key={a.to} to={a.to} className="quick-action">
            <span className="icon-tile">
              <Icon name={a.icon} />
            </span>
            <span className="grow">
              <b>{a.title}</b>
              <span>{a.sub}</span>
            </span>
          </Link>
        ))}
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'personal', label: 'Personal details' },
          { key: 'support', label: 'Support', badge: openCount || undefined },
        ]}
      />

      {/* Personal Details — shown first for every user type (consistent layout). */}
      {tab === 'personal' && (
        <div className="detail-layout">
          <div className="stack">
            <section className="card">
              <div className="card-header">
                <h2>Personal details</h2>
              </div>
              <form onSubmit={save}>
                <div className="card-body">
                  <div className="mb-3">
                    <AvatarUpload />
                  </div>
                  <Field label="Full name" id="p-name">
                    <input id="p-name" type="text" value={form.name} onChange={set('name')} required />
                  </Field>
                  <div className="grid grid-2">
                    <PhoneField id="p-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                    <Field label="Gender" id="p-gender">
                      <select id="p-gender" value={form.gender} onChange={set('gender')}>
                        <option value="">Prefer not to say</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="other">Other</option>
                      </select>
                    </Field>
                  </div>

                  <hr className="divider" />
                  <span className="t-label mb-1">Saved location</span>

                  <LocationFields
                    value={{ state: form.state, city: form.city, address: form.address, lat: form.lat, lng: form.lng }}
                    onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                    required={false}
                  />
                </div>
                <div className="card-footer">
                  <button className="btn btn-primary" disabled={busy}>
                    {busy ? <span className="spinner" /> : 'Save changes'}
                  </button>
                </div>
              </form>
            </section>

            <section className="card">
              <div className="card-header">
                <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                  <Icon name="key" size={16} />
                </span>
                <h2>Change password</h2>
              </div>
              <form onSubmit={changePassword}>
                <div className="card-body grid grid-2">
                  <Field label="Current password" id="cur-pw">
                    <input
                      id="cur-pw"
                      type="password"
                      value={pw.currentPassword}
                      onChange={(e) => setPw((p) => ({ ...p, currentPassword: e.target.value }))}
                      autoComplete="current-password"
                      required
                    />
                  </Field>
                  <Field label="New password" id="new-pw" hint="At least 6 characters.">
                    <input
                      id="new-pw"
                      type="password"
                      value={pw.newPassword}
                      onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))}
                      autoComplete="new-password"
                      minLength={6}
                      required
                    />
                  </Field>
                </div>
                <div className="card-footer">
                  <button className="btn">Update password</button>
                </div>
              </form>
            </section>
          </div>

          <aside className="stack sticky-aside">
            <section className="card">
              <div className="card-header">
                <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                  <Icon name="lock" size={16} />
                </span>
                <h3>Privacy</h3>
              </div>
              <div className="card-body">
                <p className="small muted">
                  Your address and phone number are shared with a nurse only after they accept your booking, and
                  only for that visit. Medical notes are visible to the assigned nurse alone.
                </p>
              </div>
            </section>

            <button className="btn btn-danger btn-block" onClick={logout}>
              <Icon name="logout" /> Sign out
            </button>
          </aside>
        </div>
      )}

      {/* Support — complaints. */}
      {tab === 'support' && (
        <section className="card">
          <div className="card-header">
            <h2 className="grow">Support &amp; complaints</h2>
            <button className="btn btn-sm btn-primary" onClick={() => setShowComplaint(true)}>
              <Icon name="plus" /> Raise a complaint
            </button>
          </div>
          <div className="card-body">
            {complaints.length === 0 ? (
              <Empty
                art="support"
                title="Nothing to report"
                action={
                  <button className="btn btn-primary" onClick={() => setShowComplaint(true)}>
                    Raise a complaint
                  </button>
                }
              >
                If something goes wrong with a visit, raise it here and an administrator will look into it.
              </Empty>
            ) : (
              <div className="stack-sm">
                {complaints.map((c) => (
                  <button key={c.id} type="button" className="complaint-row" onClick={() => setOpenComplaint(c)}>
                    <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                      <Icon name="megaphone" size={16} />
                    </span>
                    <div className="grow">
                      <div className="row-tight">
                        <span className="bold small grow truncate">{c.subject}</span>
                        <StatusPill status={c.status} />
                      </div>
                      <div className="tiny dim">{relativeTime(c.created_at)}</div>
                      {c.resolution && <p className="tiny muted mt-1 truncate">↳ {c.resolution}</p>}
                    </div>
                    <span className="list-chevron">
                      <Icon name="chevron-right" />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {showComplaint && (
        <Modal
          title="Raise a complaint"
          onClose={() => setShowComplaint(false)}
          size={480}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowComplaint(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!complaint.subject.trim() || !complaint.message.trim()}
                onClick={submitComplaint}
              >
                Submit
              </button>
            </>
          }
        >
          <Field label="Subject" id="c-subject">
            <input
              id="c-subject"
              value={complaint.subject}
              onChange={(e) => setComplaint((c) => ({ ...c, subject: e.target.value }))}
              placeholder="Nurse arrived late"
            />
          </Field>
          <Field label="What happened?" id="c-message">
            <textarea
              id="c-message"
              value={complaint.message}
              onChange={(e) => setComplaint((c) => ({ ...c, message: e.target.value }))}
              placeholder="Give us the details — date, nurse, and what went wrong."
            />
          </Field>
        </Modal>
      )}

      {openComplaint && (
        <ComplaintDetail
          complaint={openComplaint}
          onClose={() => setOpenComplaint(null)}
          onChange={(updated) => {
            setComplaints((list) => list.map((c) => (c.id === updated.id ? updated : c)));
            setOpenComplaint(updated);
          }}
        />
      )}
    </>
  );
}
